"use client";

import type { UseFormReturn } from "react-hook-form";

import {
  Field,
  FieldDescription,
  FieldError,
  FieldLabel,
  Input,
  NativeSelect,
  Textarea,
} from "@stocks/ui";
import { ITEM_CATEGORY_VALUES } from "@stocks/shared";

import { ITEM_CATEGORY_LABELS } from "../lib/category";
import { isSpecRequired, type ItemFormValues } from "../lib/form";

/** 품목 등록/수정 공통 입력 필드. 폼 상태는 호출한 쪽의 react-hook-form 이 가진다. */
export const ItemFormFields = ({
  form,
}: {
  form: UseFormReturn<ItemFormValues>;
}) => {
  const {
    register,
    watch,
    formState: { errors },
  } = form;
  const specRequired = isSpecRequired(watch("category"));

  return (
    <div className='flex flex-col gap-4'>
      <Field>
        <FieldLabel htmlFor='item-category'>카테고리</FieldLabel>
        <NativeSelect
          id='item-category'
          className='w-full'
          {...register("category", { required: "카테고리를 선택하세요." })}
        >
          <option value=''>선택</option>
          {ITEM_CATEGORY_VALUES.map((c) => (
            <option key={c} value={c}>
              {ITEM_CATEGORY_LABELS[c]}
            </option>
          ))}
        </NativeSelect>
        {errors.category && <FieldError>{errors.category.message}</FieldError>}
      </Field>

      <Field>
        <FieldLabel htmlFor='item-name'>품목명</FieldLabel>
        <Input
          id='item-name'
          placeholder='예: 면 40수 평직'
          {...register("name", {
            validate: (v) => v.trim() !== "" || "품목명을 입력하세요.",
          })}
        />
        {errors.name && <FieldError>{errors.name.message}</FieldError>}
      </Field>

      <Field>
        <FieldLabel htmlFor='item-spec'>
          규격{specRequired ? " (필수)" : ""}
        </FieldLabel>
        <Input
          id='item-spec'
          placeholder='예: 폭 150cm / 면 100%'
          {...register("spec", {
            validate: (v) =>
              !specRequired || v.trim() !== "" || "규격을 입력하세요.",
          })}
        />
        <FieldDescription>
          원단·라벨은 규격이 필수입니다. 공백으로 구분된 단어로 규격 차이를
          비교합니다.
        </FieldDescription>
        {errors.spec && <FieldError>{errors.spec.message}</FieldError>}
      </Field>

      <div className='grid grid-cols-2 gap-4'>
        <Field>
          <FieldLabel htmlFor='item-unit'>기준 단위</FieldLabel>
          <Input
            id='item-unit'
            placeholder='M, KG, EA …'
            {...register("baseUnit", {
              validate: (v) => v.trim() !== "" || "기준 단위를 입력하세요.",
            })}
          />
          {errors.baseUnit && <FieldError>{errors.baseUnit.message}</FieldError>}
        </Field>
        <Field>
          <FieldLabel htmlFor='item-safety'>안전재고</FieldLabel>
          <Input
            id='item-safety'
            type='number'
            min={0}
            step='any'
            {...register("safetyStock", {
              validate: (v) =>
                (v !== "" && Number(v) >= 0) || "0 이상의 숫자를 입력하세요.",
            })}
          />
          {errors.safetyStock && (
            <FieldError>{errors.safetyStock.message}</FieldError>
          )}
        </Field>
      </div>

      <Field>
        <FieldLabel htmlFor='item-desc'>설명</FieldLabel>
        <Textarea id='item-desc' rows={3} {...register("description")} />
      </Field>
    </div>
  );
};
